import aptSignal from 'AppointmentBox/signal';
import { getColorSet } from 'Calendar/helper';
import { zoomLevel } from 'Calendar/styles';
import { ICalendarHeader, ICalendarItemData, IColorSetItem } from 'Calendar/types';
import { AppointmentStatus } from 'Turn/services/constants';
import { Row } from 'antd';
import Icon from 'assets/Icons';
import { NoRequestStaff, RepeatIcon, RequestStaffHeartWhite, RequestStaffIcon } from 'assets/appointment';
import { AppointmentSourceIcon } from 'Calendar/appointment-source-icon';
import Text from 'components/Text';
import { BillStatus, CALENDAR_VIEW_TYPE, StatusColorSets } from 'constants/calendar';
import React, { memo, useCallback, useMemo } from 'react';
import aptActions from 'services/actions/apt';
import { calendarSelectors } from 'services/selectors/apt';
import shopSelectors from 'services/selectors/shop';
import { getAppointmentStyleA } from 'Calendar/appointment-style-a';
import { useAppDispatch } from 'store/hooks';
import styled, { keyframes } from 'styled-components';
import { IAppointmentItemData } from 'types/appointment';
import { FORMAT_TIME } from 'utils/format';
import { momentTimezone } from 'utils/time';
import { verifyPinCode } from 'widgets/ModalVerifyAdminPin';
import { callerSelectActive } from 'Calendar/caller-booking-selection';
import { appointmentDomId, focusedAppointmentId } from 'Calendar/appointment-focus';
import clsx from 'clsx';
import checkedInMark from 'assets/Icons/circle-check-white.svg';
import paidMark from 'assets/Icons/paid-circle-white.svg';
import confirmedMark from 'assets/Icons/confirmed-circle-white.svg';
import DraggableItem from './DnD/DraggableItem';

type IAppointmentProps = {
  data: ICalendarItemData;
  isMulti?: boolean;
  headerData?: ICalendarHeader;
  isMonthView?: boolean;
  dateOfMonth?: string;
  disabled?: boolean;
  overlay?: boolean;
};
const Appointment: React.FC<IAppointmentProps> = ({ overlay, disabled: disabledProp, data, isMulti = false, isMonthView, dateOfMonth, headerData }) => {
  const dispatch = useAppDispatch();
  const time = useMemo(() => {
    const startTime = momentTimezone(data?.startTime);
    const endTime = momentTimezone(data?.endTime);
    return `${startTime.format(FORMAT_TIME)} - ${endTime.format(FORMAT_TIME)}`;
  }, [data]);
  const extraData = useMemo(() => {
    return data.data as IAppointmentItemData;
  }, [data]);

  // Style A repaints the card from the appointment itself (request / group / note / checked-in)
  // instead of the column's rotating palette. Day view only -- week and month headers are dates, and
  // those views keep today's colors. `isMonthView` is not a substitute: it is false in week view.
  // Not applied to the multi-booking popover: its card has a fixed white ground hardcoded in
  // AppointmentStyled's `isMulti` branch, which would override the style-A ground while the text
  // still resolved to white -- white on white.
  const typeView = calendarSelectors.typeView();
  const styleA = shopSelectors.setting().appointmentStyleA
    && typeView === CALENDAR_VIEW_TYPE.DAY_VIEW
    && !isMulti;

  const styleASet = useMemo(
    () => styleA ? getAppointmentStyleA(extraData) : null,
    [styleA, extraData],
  );

  const ticketBadgeOn = shopSelectors.setting().appointmentTicketBadge;

  const checkedIn = extraData.status === AppointmentStatus.CHECK_IN;
  // Tiem chua bat feature giu nguyen hanh vi cu: mark chi ve o Style A. Bat roi thi ve
  // o ca hai che do, vi da thanh toan hay chua thi che do nao cung dang xem.
  // `status` chu khong phai `extraData.confirmed`: co do bat cho ca CHECK_IN nen dung o day
  // se khien the da check-in van deo dau CONFIRMED.
  const confirmed = ticketBadgeOn && extraData.status === AppointmentStatus.CONFIRMED;
  const showMark = ticketBadgeOn ? (checkedIn || confirmed) : !!styleASet?.checkedIn;
  // Void (2) va refund (3) tu roi khoi `paid` ma khong can nhanh rieng.
  const paid = ticketBadgeOn && checkedIn && extraData.billStatus === BillStatus.CLOSED;
  const markSrc = paid ? paidMark : confirmed ? confirmedMark : checkedInMark;
  const markAlt = paid ? 'Paid' : confirmed ? 'Confirmed' : 'Checked in';
  // Paid va Confirmed dong giua the, chi dau check-in chiem goc phai duoi. Icon goi/online
  // chi can nhuong cho khi that su co dau o goc.
  const cornerMark = showMark && !paid && !confirmed;
  const showBillNumber = ticketBadgeOn && checkedIn && extraData.billNumber != null;

  const colorSet = useMemo(() => {
    if (styleASet) return {
      background: styleASet.ground,
      aptBg: styleASet.ground,
      // A darker edge, not the ground itself: `stroke`/`shadow` draw the card border and its 3px
      // left stripe, and matching them to the ground would let two adjacent same-color cards in one
      // column merge into a single block.
      stroke: styleASet.edge,
      shadow: styleASet.edge,
      text: styleASet.text,
      // No decorative strip: the no-data-*.svg art belongs to the pastel palette and fights a flat ground.
      bottomBG: undefined,
    };
    if (extraData.status === AppointmentStatus.CHECK_IN) return StatusColorSets.checkIn;
    if (extraData.confirmed) return StatusColorSets.confirm;
    return getColorSet(headerData?.typeColor);
  }, [headerData, extraData, styleASet]);

  // A draft is a booking the server has not confirmed yet: nothing on it may be clicked, dragged or resized.
  const isDraft = !!extraData.draft;
  const disabled = disabledProp || extraData.status === AppointmentStatus.CHECK_IN || isDraft;

  const addMoreTimeAppointment: React.MouseEventHandler<HTMLButtonElement> = useCallback((e) => {
    e.stopPropagation();
    verifyPinCode.current?.checkAdmin(pinCode => {
      dispatch(aptActions.calendar.prolongedTime({ id: extraData.id, status: 'ADD', pinCode: pinCode || '' }));
    });
  }, [dispatch, extraData]);
  const minusTimeAppointment: React.MouseEventHandler<HTMLButtonElement> = useCallback((e) => {
    e.stopPropagation();
    verifyPinCode.current?.checkAdmin(pinCode => {
      dispatch(aptActions.calendar.prolongedTime({ id: extraData.id, status: 'MINUS', pinCode: pinCode || '' }));
    });
  }, [dispatch, extraData]);

  const handleOpenDetail = useCallback(() => {
    const startTime = momentTimezone(extraData.startTime);
    const endTime = momentTimezone(extraData.endTime);
    const totalDuration = endTime.diff(startTime, 'minute');
    aptSignal.openDetail({
      id: extraData.id,
      customer: {
        id: extraData.customerId,
        name: extraData.customerName,
        phone: extraData.customerPhone,
      },
      services: extraData.services,
      staffId: extraData.staffId,
      time: startTime.format(),
      totalDuration,
      note: extraData.note,
      isViewOnly: disabled,
      requestStaff: extraData.requestStaff,
      status: extraData.status,
      quickBook: extraData.quickBook,
      seriesId: extraData.seriesId,
    });
  }, [extraData, disabled]);

  const onClickDetail = useCallback(((e) => [e.stopPropagation(), handleOpenDetail()]) as React.MouseEventHandler<HTMLDivElement>, [handleOpenDetail]);

  // Booking-from-caller range mode owns the calendar — block interacting with
  // existing items (click detail, +/- time, drag) while it's active.
  const blocked = callerSelectActive.value;
  const blockedStyle = blocked ? { pointerEvents: 'none' as const } : undefined;

  // Stable object so useDraggable({ id, data }) inside DraggableItem doesn't re-subscribe each render.
  const draggableData = useMemo(() => ({ data }), [data]);

  const isSmall = useMemo(() => {
    const duration = momentTimezone(data.endTime).diff(momentTimezone(data.startTime), 'minute');
    const isSMZoom = zoomLevel.value !== 0;
    return isSMZoom && duration <= 15;
  }, [extraData, zoomLevel.value]);

  const aptId = data?.id?.toString() || '';
  // Drag overlay va ban sao trong popover multi-booking la ban sao cua cung mot lich; chi card
  // that tren luoi mang DOM id de `getElementById` khong bat nham ban sao.
  const domId = overlay || isMulti ? undefined : appointmentDomId(aptId);
  // Lich vua dat/sua sang len mot luc de nhan vien thay ngay no nam o dau.
  const focused = !!domId && focusedAppointmentId.value === aptId;

  // Plain render function (invoked as {renderContent()}), NOT a component rendered as <Content/>.
  // Rendering a freshly-defined component type each render would remount the whole subtree every time.
  const renderContent = () => {
    const customerName = extraData.customerName ? (['WALL-IN', 'WALK-IN'].find(o => extraData.customerName?.includes(o)) ? 'Walk-In' : extraData.customerName) : 'Walk-In';
    const duration = momentTimezone(data.endTime).diff(momentTimezone(data.startTime), 'minute');

    const isSmallest = zoomLevel.value === 2;
    if (isSmall) return (
      <>
        <div className={'appointment-box appointment-box-sm'} style={{ padding: '0px 3px', paddingBottom: '6px' }}>
          <div style={{ display: 'flex', alignItems: 'start', justifyContent: 'space-between', gap: '4px' }} className='text-ellipsis'>
            <div style={{ textAlign: 'left', flex: 1 }}>
              <Text className='customerName appointment-name-sm' variant='CONTENT_2' color='text_3' style={{ fontWeight: '600' }}>{customerName?.slice(0, 11)}{customerName.length > 11 ? '...' : ''}</Text>
              {!isSmallest && <RowTime align={'middle'}>
                <Text className='time appointment-time-sm' variant='CAPTION_3' color='text_3' style={{ fontWeight: '600' }}>{time}</Text>
              </RowTime>}
            </div>
            <div>
            </div>
            {extraData.seriesId && <div className='appointment-icon-sm' style={{ display: 'flex', paddingTop: '1px' }}><RepeatIcon /></div>}
            {styleASet
              ? (styleASet.showHeart && <div className='appointment-icon-sm' style={{ display: 'flex', paddingTop: '1px' }}><RequestStaffHeartWhite /></div>)
              : (extraData.requestStaff ? (<div className='appointment-icon-sm' style={{ display: 'flex', paddingTop: '1px' }}><RequestStaffIcon /></div>) : <div className='appointment-icon-sm' style={{ display: 'flex', paddingTop: '1px' }}><NoRequestStaff /></div>)}
            <AppointmentType small $leftCorner={!!styleASet || cornerMark} className='appointment-icon-sm'>
              <AppointmentSourceIcon data={extraData} />
              {showBillNumber && <BillNumberBadge>#{extraData.billNumber}</BillNumberBadge>}
            </AppointmentType>
          </div>
          <Text className='text-ellipsis service appointment-service-sm' variant='CAPTION_3' color='text_3' textAlign='left'>
            <b>Sers:</b> {extraData.services.map(o => o.serviceName || '').join(',')}
            {!styleASet && extraData.note && <><b> Notes:</b> {extraData.note}</>}
          </Text>
          {/* One line only on a short card, and nothing at all at the smallest zoom -- there is no
              vertical room left for a chip there. */}
          {styleASet?.noteChip && !isSmallest && (
            <NoteChip className='appointment-note-chip' $bg={styleASet.noteChipBg} $color={styleASet.noteChipText} $lines={1}>
              {styleASet.noteChip}
            </NoteChip>
          )}
        </div>
        {showMark && <CheckedInMark $stamp={paid || confirmed} $wide={confirmed} src={markSrc} alt={markAlt} />}
        {!disabled && !isMonthView &&
          <Row className="bottom appointment-bottom-action" align={'middle'} justify={'center'} style={{ gap: isSmallest ? '0.5rem' : '1rem' }}>
            {duration > 15 && <Row align={'middle'} justify={'center'} ><button onClick={minusTimeAppointment}><Icon type='minusAppointment' /></button></Row>}
            <Row align={'middle'} justify={'center'} ><button onClick={addMoreTimeAppointment}><Icon type='addAppointment' /></button></Row>
          </Row>
        }
      </>
    );

    return (
      <>
        <div className={'appointment-box'}>
          <Row style={{ margin: 0 }}>
            <RowTime align={'middle'} style={{ flex: 1 }}>
              <Text className='time appointment-time' variant='CAPTION_3' color='text_3' style={{ fontWeight: '600' }}>{time}</Text>
            </RowTime>
            {/* Invisible spacer reserving width for the absolutely-positioned badge above. Style A
                shows a badge only for a request, so the spacer follows the same condition. */}
            {styleASet
              ? (styleASet.showHeart && <div className='appointment-icon' style={{ opacity: 0 }}><RequestStaffHeartWhite /></div>)
              : (extraData.requestStaff ? (<div className='appointment-icon' style={{ opacity: 0 }}><RequestStaffIcon /></div>) : <div style={{ opacity: 0 }} className='appointment-icon'><NoRequestStaff /></div>)}
            <div style={{ position: 'absolute', right: '4px', top: '2px', gap: '2px', display: 'flex', alignItems: 'center' }}>
              {extraData.seriesId && <div className='appointment-icon'><RepeatIcon /></div>}
              {styleASet
                ? (styleASet.showHeart && <div className='appointment-icon'><RequestStaffHeartWhite /></div>)
                : (extraData.requestStaff ? <div className='appointment-icon'><RequestStaffIcon /></div> : <div className='appointment-icon'><NoRequestStaff /></div>)}
            </div>
          </Row>
          {dateOfMonth && <Text variant='BODY_1' color='text_2' mb={1} p={0.25} textAlign='left'>{dateOfMonth}</Text>}
          <div style={{ textAlign: 'left' }}>
            <Text className='customerName appointment-name' variant='CONTENT_2' color='text_3' style={{ fontWeight: '600' }}>{customerName}</Text>
          </div>
          <Text className='service appointment-service' variant='CAPTION_3' color='text_3' style={{ textAlign: 'left' }}>{extraData.services.map(o => o.serviceName || '').join(', ')}</Text>

          {styleASet ? (
            styleASet.noteChip && (
              <NoteChip className='appointment-note-chip' $bg={styleASet.noteChipBg} $color={styleASet.noteChipText} $lines={2}>
                {styleASet.noteChip}
              </NoteChip>
            )
          ) : (extraData.note && <div style={{ textAlign: 'left' }}>
            <Text className='service appointment-service' variant='CAPTION_3' color='text_3' textAlign='left' >
              <b>Note:</b> {extraData.note}</Text>
          </div>)}
          <div className="flex-grow" />
        </div>
        {!isMonthView && <>
          {isMulti || !!overlay ? null : <div style={{
            height: 20,
            width: '100%',
            position: 'absolute',
            bottom: 0,
            backgroundImage: colorSet.bottomBG ? `url(${colorSet.bottomBG})` : undefined,
            backgroundColor: colorSet.aptBg,
            boxShadow: `inset 3px 0px 0px 0px ${colorSet.shadow}`,
            borderBottomRightRadius: 5,
            borderBottomLeftRadius: 5,
            borderBottom: `0.5px solid ${colorSet.shadow}`,
          }}></div>}
        </>}
        {!disabled && !isMonthView &&
          <>
            <Row className="bottom appointment-bottom-action" align={'middle'} justify={'center'} style={{
              gap: isSmallest ? '0.5rem' : '1rem',
            }}>
              {duration > 15 && <Row align={'middle'} justify={'center'} ><button onClick={minusTimeAppointment}><Icon type='minusAppointment' /></button></Row>}
              <Row align={'middle'} justify={'center'} ><button onClick={addMoreTimeAppointment}><Icon type='addAppointment' /></button></Row>
            </Row>
          </>
        }
        <AppointmentType isMulti={isMulti} small $leftCorner={!!styleASet || cornerMark} className='appointment-icon'>
          <AppointmentSourceIcon data={extraData} />
          {showBillNumber && <BillNumberBadge>#{extraData.billNumber}</BillNumberBadge>}
        </AppointmentType>
        {showMark && <CheckedInMark $stamp={paid || confirmed} $wide={confirmed} src={markSrc} alt={markAlt} />}
      </>
    );
  };

  if (extraData.status === AppointmentStatus.CHECK_IN) {
    return (
      <AppointmentStyled
        id={domId}
        className={clsx('AppointmentStyled', focused && 'apt-focused')}
        isAnybody={extraData.isAnybody || extraData.requestStaff}
        isMulti={isMulti}
        colorSet={colorSet}
        $statusMode={!!styleASet || extraData.status === AppointmentStatus.CHECK_IN}
        onClick={onClickDetail}
        style={blockedStyle}
      >
        {renderContent()}
      </AppointmentStyled>
    );
  }

  if (isDraft) {
    return (
      <AppointmentStyled
        id={domId}
        className={clsx('AppointmentStyled', 'apt-draft')}
        isAnybody={extraData.isAnybody || extraData.requestStaff}
        isMulti={isMulti}
        colorSet={colorSet}
        $statusMode={!!styleASet}
        style={draftStyle}
      >
        {renderContent()}
        <DraftLabel>Saving…</DraftLabel>
      </AppointmentStyled>
    );
  }

  return (
    <DraggableItem
      key={data?.id}
      id={data?.id?.toString()}
      data={draggableData}
    >
      <AppointmentStyled
        id={domId}
        className={clsx('AppointmentStyled', focused && 'apt-focused')}
        isAnybody={extraData.isAnybody || extraData.requestStaff}
        isMulti={isMulti}
        colorSet={colorSet}
        $statusMode={!!styleASet || extraData.confirmed}
        onClick={onClickDetail}
        style={blockedStyle}
      >
        {renderContent()}
      </AppointmentStyled>
    </DraggableItem >
  );
};

export default memo(Appointment);

// Muted and dashed so a draft reads as "not there yet"; pointer-events off blocks every handler on it.
const draftStyle: React.CSSProperties = { opacity: 0.6, outline: '2px dashed #999', outlineOffset: -2, pointerEvents: 'none' };
const DraftLabel = styled.span`
  position: absolute;
  top: 2px;
  right: 4px;
  font-size: 10px;
  font-weight: 600;
  color: #555;
  background: rgba(255, 255, 255, 0.85);
  border-radius: 3px;
  padding: 0 4px;
  z-index: 2;
`;
type AppointmentStyledProps = {
  isMulti: boolean;
  isAnybody?: boolean;
  $statusMode?: boolean;
  colorSet?: IColorSetItem | null;
}
// Vien nhap nhay cho lich vua dat/sua. Dung box-shadow tren wrapper nen khong day layout va
// khong de len box-shadow inset dang ve vien mau cua `.appointment-box`.
const focusPulse = keyframes`
  0%, 100% { box-shadow: 0 0 0 3px rgba(240, 83, 38, 0.95); }
  50% { box-shadow: 0 0 0 7px rgba(240, 83, 38, 0.3); }
`;
const AppointmentStyled = styled.div<AppointmentStyledProps>`
  height: calc(100% - 12px);
  width: 100%;
  display: flex;
  position: relative;
  align-self: stretch;

  &.apt-focused {
    z-index: 2;
    border-radius: 6px;
    animation: ${focusPulse} 0.9s ease-in-out 3;
  }
  
  .appointment-box {
    background: #fff;
    border-radius: 5px;
    overflow: hidden;

    display: flex;
    flex-direction: column;
    justify-content: space-between;
    padding: 0px 4px;
    padding-bottom: 1.25rem;
    flex: 1;

    ${({ colorSet }) => colorSet ? `
      border-radius: 5px;
      background: ${colorSet.aptBg};
      border: 0.5px solid ${colorSet.shadow};
      border-bottom: 0;
      box-shadow: inset 3px 0px 0px 0px ${colorSet.shadow};
    `: ''}

    ${({ isMulti }) => isMulti ? `
      border-radius: 5px;
      background: #FFFFFF;
      border: 0.5px solid #95B8D1;
      box-shadow: inset 3px 0px 0px 0px #0D446C;
      padding: 4px 8px;
    `: ''}
    .flex-grow {
      flex:1;
    }


    ${({ $statusMode, colorSet }) => $statusMode ? `
      .appointment-time, .appointment-service, .appointment-name,
      .appointment-time-sm, .appointment-service-sm, .appointment-name-sm {
        color: ${colorSet?.text || '#fff'};
      }
    `: ''}
  }
  .apt-online {
    background: #E7FBFF;
  }
  .bottom {
    position: absolute;
    bottom:0;
    width:100%;
    transform: translateY(16px);
    button {
      border-radius: 100%;
    }
  }

  .customerName {
    color: #1D2129;
    font-family: Poppins;
    font-size: 14px;
    font-style: normal;
    font-weight: 500;
    line-height: normal;
  }

  .service {
    overflow: hidden;
    color: #1D2129;
    text-overflow: ellipsis;
    font-family: Poppins;
    font-size: 14px;
    font-style: normal;
    font-weight: 400;
    line-height: normal;
  }
`;

/**
 * Purple chip carrying an appointment note under style A. Clamped rather than ellipsised on one line
 * so a two-line note stays readable, but never grows the card past its slot: `$lines` drops to 1 on
 * the short-card branch.
 */
const NoteChip = styled.div<{ $bg: string; $color: string; $lines: number }>`
  margin: 2px 0;
  padding: 2px 6px;
  border-radius: 4px;
  text-align: left;
  font-family: Poppins;
  font-size: 12px;
  font-weight: 500;
  line-height: 1.3;
  background: ${({ $bg }) => $bg};
  color: ${({ $color }) => $color};
  display: -webkit-box;
  -webkit-line-clamp: ${({ $lines }) => $lines};
  -webkit-box-orient: vertical;
  overflow: hidden;
  word-break: break-word;
`;

/**
 * Check-in mark, bottom-right. Replaces a text badge so the card keeps its full colour and the state
 * still reads at a glance; the calling icon moves to the opposite corner to make room.
 *
 * Paid is the end state of the card rather than one more attribute of it, so it reads from the
 * middle at 2.5x instead of sharing the corner with everything else -- the artwork is a ring around
 * open space, so it stamps over the text rather than hiding it.
 */
const CheckedInMark = styled.img<{ $stamp?: boolean; $wide?: boolean }>`
  position: absolute;
  right: 4px;
  bottom: 4px;
  width: 42px;
  height: 42px;
  pointer-events: none;

  /* Paid va Confirmed deu la con dau dong giua the, khong phai huy hieu goc nhu dau check-in. */
  ${({ $stamp }) => $stamp && `
    right: unset;
    bottom: 10%;
    left: 50%;
    transform: translateX(-50%);
    width: 105px;
    height: 105px;
    /* A 15-minute row at the smallest zoom is shorter than the stamp itself, so cap it against the
       card and let it shrink there instead of spilling onto the appointments above and below. */
    max-width: 50%;
    max-height: 50%;
    object-fit: contain;
  `}

  /* Dau CONFIRMED rong 2:1 vi chu dai; o vuong 105px se khien no be bang nua dau PAID. */
  ${({ $wide }) => $wide && `
    width: 150px;
    height: 75px;
    max-width: 72%;
    max-height: 36%;
  `}
`;

/**
 * So ticket sinh khi check-in. Nen den co dinh thay vi mau theo card: card doi mau
 * theo request/group/status nen chi mot nen trung tinh moi giu duoc tuong phan tren
 * ca bon nen (do, xanh la, xanh duong, va palette legacy).
 */
const BillNumberBadge = styled.span`
  background: #000000;
  color: #FFFFFF;
  font-family: Poppins;
  font-size: 20px;
  font-weight: 600;
  line-height: 1;
  padding: 2px 5px;
  border-radius: 4px;
  white-space: nowrap;
  pointer-events: none;
`;

const RowTime = styled(Row)`
  gap: 4px;
  .time {
    color: #7B7B7B;
    font-family: Poppins;
    font-size: 14px;
    font-style: normal;
    font-weight: 500;
    line-height: normal;
  }
`;

// const TimeLock = () => (
//   <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
//     <path d="M10 18.75C8.26942 18.75 6.57769 18.2368 5.13876 17.2754C3.69983 16.3139 2.57832 14.9473 1.91606 13.3485C1.25379 11.7496 1.08051 9.9903 1.41813 8.29296C1.75575 6.59563 2.58911 5.03653 3.81282 3.81282C5.03653 2.58911 6.59563 1.75575 8.29296 1.41813C9.9903 1.08051 11.7496 1.25379 13.3485 1.91606C14.9473 2.57832 16.3139 3.69983 17.2754 5.13876C18.2368 6.57769 18.75 8.26942 18.75 10C18.75 12.3206 17.8281 14.5462 16.1872 16.1872C14.5462 17.8281 12.3206 18.75 10 18.75ZM10 2.5C8.51664 2.5 7.0666 2.93987 5.83323 3.76398C4.59986 4.58809 3.63856 5.75943 3.07091 7.12988C2.50325 8.50032 2.35473 10.0083 2.64411 11.4632C2.9335 12.918 3.64781 14.2544 4.6967 15.3033C5.7456 16.3522 7.08197 17.0665 8.53683 17.3559C9.99168 17.6453 11.4997 17.4968 12.8701 16.9291C14.2406 16.3614 15.4119 15.4002 16.236 14.1668C17.0601 12.9334 17.5 11.4834 17.5 10C17.5 8.01088 16.7098 6.10323 15.3033 4.6967C13.8968 3.29018 11.9891 2.5 10 2.5Z" fill="#F05326" />
//     <path d="M12.8688 13.75L9.375 10.2563V4.375H10.625V9.7375L13.75 12.8688L12.8688 13.75Z" fill="#F05326" />
//   </svg>
// );

const AppointmentType = styled.div<{ small?: boolean, isMulti?: boolean, $leftCorner?: boolean }>`
  position: absolute;
  right: 8px;
  top: 4px;
  gap: 4px;
  display: flex;
  align-items: center;
  ${({ small }) => small && `
  position: absolute;
  right: 4px;
  top:unset;
  bottom: 2px;
  gap: 2px;
    svg {
      width: 24px !important;
    }
  `}

  ${({ isMulti }) => isMulti && `
    top: 4px;
    gap: 4px;
    bottom: unset;
  `}

  /* Style A puts the check-in mark in the bottom-right corner, so this one moves out of its way. */
  ${({ $leftCorner }) => $leftCorner && `
    right: unset;
    left: 4px;
  `}
`;
